import React, { useState } from 'react';

/**
 * CaptainTransferModal - Transfer captaincy before substituting current captain
 * 
 * @param {boolean} isOpen - Modal visibility
 * @param {object} currentCaptain - Current captain being substituted
 * @param {array} eligiblePlayers - Active starting players (excluding current captain)
 * @param {function} onTransfer - Callback with new captain playerId
 * @param {function} onCancel - Close modal
 */
export const CaptainTransferModal = ({ 
  isOpen, 
  currentCaptain, 
  eligiblePlayers, 
  onTransfer, 
  onCancel 
}) => {
  const [selectedNewCaptain, setSelectedNewCaptain] = useState(null);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!selectedNewCaptain) return;
    onTransfer(selectedNewCaptain);
    setSelectedNewCaptain(null);
  };

  const handleCancel = () => {
    setSelectedNewCaptain(null);
    onCancel();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-2xl font-bold mb-2">Transfer Captaincy</h2>
        <p className="text-gray-600 mb-4">
          Captain must transfer captaincy before being substituted
        </p>
        
        {/* Current Captain */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Current Captain
          </label>
          <div className="p-3 bg-yellow-50 border-2 border-yellow-400 rounded-lg">
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-gray-700">
                #{currentCaptain.jersey_number}
              </span>
              <span className="font-semibold text-gray-800">
                {currentCaptain.player.first_name} {currentCaptain.player.last_name}
              </span>
              <span className="px-2 py-0.5 bg-yellow-400 text-yellow-900 text-xs font-bold rounded">
                C
              </span>
            </div>
          </div>
        </div>

        {/* New Captain Selection */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Select New Captain
          </label>
          
          {eligiblePlayers.length === 0 ? (
            <div className="p-4 bg-gray-100 rounded-lg text-center text-gray-600">
              No eligible players available
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {eligiblePlayers.map((playerPos) => (
                <button
                  key={playerPos.id}
                  onClick={() => setSelectedNewCaptain(playerPos)}
                  className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                    selectedNewCaptain?.id === playerPos.id
                      ? 'bg-yellow-50 border-yellow-500'
                      : 'bg-white border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-gray-700">
                      #{playerPos.jersey_number}
                    </span>
                    <span className="font-semibold text-gray-800">
                      {playerPos.player.first_name} {playerPos.player.last_name}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleCancel}
            className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedNewCaptain}
            className="flex-1 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-semibold"
          >
            Transfer Captaincy
          </button>
        </div>
      </div>
    </div>
  );
};
